import { boundMethod } from 'autobind-decorator';
import type {
  AsyncStatus,
  PhoneNumber,
  Response,
} from 'lib-shared-legacy-types';
import memoize from 'memoize-one';
import { ComponentType, PureComponent } from 'react';

import { omitNullValues } from 'utils/object';
import { NullableValues } from 'utils/typeUtils';

import telephony from './api/telephony';
import type {
  BuyGroupQuery,
  BuyGroupResponse,
  BuyNumberQuery,
  BuyNumberResponse,
  PhoneNumbersGetItemsResponse,
  PhoneNumbersQueryParams,
  PhoneNumbersReleaseResponse,
  UpdateNumberQuery,
  UpdateNumberResponse,
} from './api/types';

const DEFAULT_FETCH_THROTTLE = 300;

export interface WithPhoneNumbersInjectedProps {
  phoneNumbers: WithPhoneNumbersState & WithPhoneNumbersMethods;
}

interface WithPhoneNumbersMethods {
  getItems: () => Promise<PhoneNumbersGetItemsResponse>;
  paramsChange: (
    params: NullableValues<PhoneNumbersQueryParams>,
    fetchThrottle?: number,
  ) => Promise<PhoneNumbersGetItemsResponse | void>;
  nextPage: () => Promise<PhoneNumbersGetItemsResponse>;
  previousPage: () => Promise<PhoneNumbersGetItemsResponse>;
  release: (number: PhoneNumber) => Promise<PhoneNumbersReleaseResponse>;
  update: (query: UpdateNumberQuery) => Promise<UpdateNumberResponse>;
  buyNumber: (query: BuyNumberQuery) => Promise<BuyNumberResponse>;
  buyGroup: (query: BuyGroupQuery) => Promise<BuyGroupResponse>;
}

interface WithPhoneNumbersAsyncStatus {
  getItemsAsyncStatus: AsyncStatus;
  releaseAsyncStatus: AsyncStatus;
  updateAsyncStatus: AsyncStatus;
  purchaseAsyncStatus: AsyncStatus;
}

interface WithPhoneNumbersState extends WithPhoneNumbersAsyncStatus {
  items: Array<PhoneNumber>;
  params: PhoneNumbersQueryParams;
  hasNextPage: boolean | null;
  hasPreviousPage: boolean | null;
  nextPagePath: string | null;
  previousPagePath: string | null;
}

type WithPhoneNumbersNotInjectedProps<T> = Omit<
  T,
  keyof WithPhoneNumbersInjectedProps
>;

/** The position of the new data, used when `settings.infinite` is `true` */
enum NewDataPosition {
  PreviousPage = -1,
  Replace = 0,
  NextPage = 1,
}

interface WithPhoneNumbersSettings {
  initialParams?: PhoneNumbersQueryParams;
  infinite?: boolean;
}

export const withPhoneNumbers = <T extends WithPhoneNumbersInjectedProps>(
  Component: ComponentType<T>,
  settings: WithPhoneNumbersSettings = {},
): ComponentType<WithPhoneNumbersNotInjectedProps<T>> => {
  class WithPhoneNumbers
    extends PureComponent<
      WithPhoneNumbersNotInjectedProps<T>,
      WithPhoneNumbersState
    >
    implements WithPhoneNumbersMethods
  {
    state: WithPhoneNumbersState = {
      items: [],
      getItemsAsyncStatus: 'succeeded',
      releaseAsyncStatus: 'succeeded',
      updateAsyncStatus: 'succeeded',
      purchaseAsyncStatus: 'succeeded',
      params: settings.initialParams ?? {},
      hasNextPage: null,
      hasPreviousPage: null,
      nextPagePath: null,
      previousPagePath: null,
    };

    timeout: ReturnType<typeof setTimeout> | null = null;

    memoizedGetInjectedProps = memoize(this.getInjectedProps).bind(this);

    @boundMethod
    getItems(
      position: NewDataPosition = NewDataPosition.Replace,
    ): Promise<PhoneNumbersGetItemsResponse> {
      const { params } = this.state;

      this.setState({ getItemsAsyncStatus: 'loading' });

      return telephony
        .getPhoneNumbers(params)
        .then(
          (res) => this.handleQueryResolve(res, position),
          this.handleQueryReject,
        );
    }

    @boundMethod
    paramsChange(
      params?: NullableValues<PhoneNumbersQueryParams>,
      fetchThrottle: number = DEFAULT_FETCH_THROTTLE,
    ) {
      return new Promise<PhoneNumbersGetItemsResponse | void>((resolve) => {
        this.setState(
          (state) => ({
            // remove values that are `nulled` by the new param
            params: omitNullValues({
              ...state.params,
              ...params,
            }) as PhoneNumbersQueryParams,
          }),
          () => {
            if (fetchThrottle >= 0) {
              if (this.timeout) {
                clearTimeout(this.timeout);
              }

              this.timeout = setTimeout(() => {
                resolve(this.getItems());
              }, fetchThrottle);
            } else {
              resolve();
            }
          },
        );
      });
    }

    @boundMethod
    release(number: PhoneNumber): Promise<PhoneNumbersReleaseResponse> {
      const handlers =
        this.getAsyncStatusUpdateHandlers<PhoneNumbersReleaseResponse>(
          'releaseAsyncStatus',
        );

      this.setState({ releaseAsyncStatus: 'loading' });

      return telephony.releaseNumber(number).then(...handlers);
    }

    @boundMethod
    update(query: UpdateNumberQuery): Promise<UpdateNumberResponse> {
      const handlers =
        this.getAsyncStatusUpdateHandlers<UpdateNumberResponse>(
          'updateAsyncStatus',
        );

      this.setState({ updateAsyncStatus: 'loading' });

      return telephony.updateNumber(query).then(...handlers);
    }

    @boundMethod
    buyNumber(query: BuyNumberQuery): Promise<BuyNumberResponse> {
      const handlers = this.getAsyncStatusUpdateHandlers<BuyNumberResponse>(
        'purchaseAsyncStatus',
      );

      this.setState({ purchaseAsyncStatus: 'loading' });

      return telephony.buyNumber(query).then(...handlers);
    }

    @boundMethod
    buyGroup(query: BuyGroupQuery): Promise<BuyGroupResponse> {
      const handlers = this.getAsyncStatusUpdateHandlers<BuyGroupResponse>(
        'purchaseAsyncStatus',
      );

      this.setState({ purchaseAsyncStatus: 'loading' });

      return telephony.buyGroup(query).then(...handlers);
    }

    @boundMethod
    getAsyncStatusUpdateHandlers<TResponse extends Response<unknown, unknown>>(
      key: keyof WithPhoneNumbersAsyncStatus,
    ) {
      const handler = this.handleAsyncStatusUpdate<TResponse>(key);

      return [handler('succeeded'), handler('failed')];
    }

    handleAsyncStatusUpdate =
      <TResponse extends Response<unknown, unknown>>(
        key: keyof WithPhoneNumbersAsyncStatus,
      ) =>
      (asyncStatus: AsyncStatus) =>
      (response: TResponse): TResponse => {
        this.setState({ [key]: asyncStatus } as Pick<
          WithPhoneNumbersState,
          keyof WithPhoneNumbersAsyncStatus
        >);

        return response;
      };

    @boundMethod
    handleQueryResolve(
      response: PhoneNumbersGetItemsResponse,
      position: NewDataPosition = NewDataPosition.Replace,
    ) {
      const { data, meta } = response;

      this.setState(({ items }) => ({
        getItemsAsyncStatus: 'succeeded',
        items: this.getPaginatedItems(data.numbers, items, position),
        nextPagePath: meta.next_page,
        previousPagePath: meta.prev_page,
        hasNextPage: !!meta.next_page,
        hasPreviousPage: !!meta.prev_page,
      }));

      return response;
    }

    @boundMethod
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    handleQueryReject(error: any) {
      this.setState({ getItemsAsyncStatus: 'failed' });

      return error;
    }

    getPaginatedItems(
      newItems: PhoneNumber[],
      oldItems: PhoneNumber[],
      position: NewDataPosition = NewDataPosition.Replace,
    ): PhoneNumber[] {
      if (!settings.infinite || position === NewDataPosition.Replace) {
        return newItems;
      }

      if (position === NewDataPosition.NextPage) {
        return oldItems.concat(newItems);
      }

      if (position === NewDataPosition.PreviousPage) {
        return newItems.concat(oldItems);
      }

      return [];
    }

    @boundMethod
    changePath(
      key: 'nextPagePath' | 'previousPagePath',
      position: NewDataPosition,
    ): Promise<PhoneNumbersGetItemsResponse> {
      return new Promise((resolve, reject) => {
        this.setState(
          ({ [key]: path }) => {
            if (!path) {
              throw new Error(`${key} does not exist`);
            }

            return { getItemsAsyncStatus: 'loading', params: { path } };
          },
          () => void this.getItems(position).then(resolve, reject),
        );
      });
    }

    @boundMethod
    nextPage(): Promise<PhoneNumbersGetItemsResponse> {
      return this.changePath('nextPagePath', NewDataPosition.NextPage);
    }

    @boundMethod
    previousPage(): Promise<PhoneNumbersGetItemsResponse> {
      return this.changePath('previousPagePath', NewDataPosition.PreviousPage);
    }

    @boundMethod
    getInjectedProps(
      items: Readonly<WithPhoneNumbersState>['items'],
      getItemsAsyncStatus: Readonly<WithPhoneNumbersState>['getItemsAsyncStatus'],
      releaseAsyncStatus: Readonly<WithPhoneNumbersState>['releaseAsyncStatus'],
      updateAsyncStatus: Readonly<WithPhoneNumbersState>['updateAsyncStatus'],
      purchaseAsyncStatus: Readonly<WithPhoneNumbersState>['purchaseAsyncStatus'],
      params: Readonly<WithPhoneNumbersState>['params'],
      hasNextPage: Readonly<WithPhoneNumbersState>['hasNextPage'],
      hasPreviousPage: Readonly<WithPhoneNumbersState>['hasPreviousPage'],
      nextPagePath: Readonly<WithPhoneNumbersState>['nextPagePath'],
      previousPagePath: Readonly<WithPhoneNumbersState>['previousPagePath'],
    ): WithPhoneNumbersInjectedProps['phoneNumbers'] {
      return {
        items,
        getItemsAsyncStatus,
        releaseAsyncStatus,
        updateAsyncStatus,
        purchaseAsyncStatus,
        params,
        hasNextPage,
        hasPreviousPage,
        nextPagePath,
        previousPagePath,
        getItems: this.getItems,
        paramsChange: this.paramsChange,
        nextPage: this.nextPage,
        previousPage: this.previousPage,
        release: this.release,
        update: this.update,
        buyNumber: this.buyNumber,
        buyGroup: this.buyGroup,
      };
    }

    render() {
      const {
        items,
        getItemsAsyncStatus,
        releaseAsyncStatus,
        updateAsyncStatus,
        purchaseAsyncStatus,
        params,
        hasNextPage,
        hasPreviousPage,
        nextPagePath,
        previousPagePath,
      } = this.state;

      return (
        <Component
          {...(this.props as T)}
          phoneNumbers={this.memoizedGetInjectedProps(
            items,
            getItemsAsyncStatus,
            releaseAsyncStatus,
            updateAsyncStatus,
            purchaseAsyncStatus,
            params,
            hasNextPage,
            hasPreviousPage,
            nextPagePath,
            previousPagePath,
          )}
        />
      );
    }
  }

  return WithPhoneNumbers;
};
